<div class="space-y-6">
  <app-page-header [backTo]="['/roles']" backLabel="Retour à la liste" />

  @if (loadError() || loading()) {
    <app-data-state
      [loading]="loading()"
      [error]="loadError()"
      (retry)="fetch()"
    />
  } @else if (role(); as r) {
    <section class="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
      <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div class="flex items-center gap-2">
            <h1 class="font-mono text-title-sm font-bold text-gray-800 dark:text-white/90 sm:text-title-md">
              {{ r.name }}
            </h1>
            <span
              class="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium"
              [class.bg-brand-50]="isBusinessRole()"
              [class.text-brand-700]="isBusinessRole()"
              [class.dark:bg-brand-500/15]="isBusinessRole()"
              [class.dark:text-brand-400]="isBusinessRole()"
              [class.bg-gray-100]="!isBusinessRole()"
              [class.text-gray-600]="!isBusinessRole()"
              [class.dark:bg-gray-800]="!isBusinessRole()"
              [class.dark:text-gray-400]="!isBusinessRole()"
            >
              {{ isBusinessRole() ? 'métier' : 'système' }}
            </span>
          </div>
          @if (r.description) {
            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">{{ r.description }}</p>
          }
          <div class="mt-2 flex flex-wrap items-center gap-2">
            @if (r.composite) {
              <span class="inline-flex items-center gap-1 rounded-full bg-warning-50 px-2 py-0.5 text-[10px] font-medium text-warning-700 dark:bg-warning-500/15 dark:text-warning-400">Composite</span>
            }
            @if (r.clientRole) {
              <span class="inline-flex items-center gap-1 rounded-full bg-blue-light-50 px-2 py-0.5 text-[10px] font-medium text-blue-light-700 dark:bg-blue-light-500/15 dark:text-blue-light-400">Client</span>
            } @else {
              <span class="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-400">Realm</span>
            }
          </div>
        </div>

        <div class="flex flex-wrap gap-2">
          <a
            [routerLink]="['/roles', r.name, 'edit']"
            class="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5"
          >
            Modifier
          </a>
          <button
            type="button"
            (click)="openDelete()"
            class="inline-flex items-center gap-2 rounded-lg border border-error-300 px-4 py-2 text-sm font-medium text-error-700 transition hover:bg-error-50 dark:border-error-800 dark:text-error-400 dark:hover:bg-error-500/10"
          >
            Supprimer
          </button>
        </div>
      </div>
    </section>

    <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <section class="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
        <h2 class="mb-4 text-sm font-semibold text-gray-800 dark:text-white/90">Identifiants</h2>
        <dl class="grid grid-cols-1 gap-4">
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">ID Keycloak</dt>
            <dd class="mt-1 break-all font-mono text-xs text-gray-700 dark:text-gray-300">{{ r.id || '—' }}</dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Container ID</dt>
            <dd class="mt-1 break-all font-mono text-xs text-gray-700 dark:text-gray-300">{{ r.containerId || '—' }}</dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Politique liée</dt>
            <dd class="mt-1 text-sm text-gray-700 dark:text-gray-300">{{ r.policyName || r.policyId || '—' }}</dd>
          </div>
        </dl>
      </section>

      <section class="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
        <h2 class="mb-4 text-sm font-semibold text-gray-800 dark:text-white/90">Attributs</h2>
        @if (attributeEntries().length) {
          <dl class="space-y-3">
            @for (e of attributeEntries(); track e.key) {
              <div>
                <dt class="font-mono text-xs text-gray-500 dark:text-gray-400">{{ e.key }}</dt>
                <dd class="mt-1 flex flex-wrap gap-1">
                  @for (v of e.values; track v) {
                    <span class="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-700 dark:bg-gray-800 dark:text-gray-300">{{ v }}</span>
                  }
                </dd>
              </div>
            }
          </dl>
        } @else {
          <p class="text-xs text-gray-500 dark:text-gray-400">Aucun attribut.</p>
        }
      </section>

      <section class="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03] lg:col-span-2">
        <h2 class="mb-4 text-sm font-semibold text-gray-800 dark:text-white/90">Permissions rattachées</h2>
        @if (r.permissionIds?.length) {
          <ul class="flex flex-wrap gap-1.5">
            @for (pid of r.permissionIds; track pid) {
              <li>
                <span class="inline-flex items-center rounded-md bg-gray-100 px-2 py-1 font-mono text-[11px] text-gray-700 dark:bg-gray-800 dark:text-gray-300">{{ pid }}</span>
              </li>
            }
          </ul>
        } @else {
          <p class="text-xs text-gray-500 dark:text-gray-400">
            Aucune permission rattachée. La gestion fine des permissions arrive dans la prochaine itération.
          </p>
        }
      </section>
    </div>
  }
</div>

<app-confirm-modal
  [open]="deleteOpen()"
  [busy]="deleting()"
  [error]="deleteError()"
  title="Supprimer ce rôle ?"
  confirmLabel="Supprimer définitivement"
  busyLabel="Suppression…"
  danger
  (confirm)="confirmDelete()"
  (close)="closeDelete()"
>
  Le rôle <span class="font-mono font-medium text-gray-700 dark:text-gray-300">{{ role()?.name }}</span> sera retiré de Keycloak. Les utilisateurs qui l'ont assigné le perdront.
</app-confirm-modal>
